// CSS for the Madeira Mobile Mechanic site. Mirrors the rules embedded in
// source/index.html so the React renderer can scope and inject them without
// importing the raw HTML file. Keep these two in sync if you change one.

const sourceStyles = `
:root {
  --deep:          #18243A;
  --navy:          #2C3F58;
  --navy-mid:      #3D5070;
  --copper:        #E8915A;
  --copper-light:  #F0A870;
  --copper-dark:   #C97A45;
  --cream:         #F0EBE2;
  --cream-dim:     #C0B8AD;
  --silver:        #8C9BAD;
  --muted:         #5E6E80;
  --border:        rgba(140,155,173,0.18);
  --border-cu:     rgba(232,145,90,0.25);
}

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
html { scroll-behavior: smooth; }

body {
  background: var(--deep);
  color: var(--cream);
  font-family: 'Barlow', sans-serif;
  font-weight: 300;
  overflow-x: hidden;
}

body::after {
  content: '';
  position: fixed;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
  opacity: 0.035;
  pointer-events: none;
  z-index: 9999;
}

h1, h2, h3 { font-family: 'Cormorant Garamond', serif; }

.label {
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--copper);
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }

.copper-line {
  width: 50px; height: 1px;
  background: var(--copper);
  margin: 1.25rem 0 1.75rem;
}

.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.75s ease, transform 0.75s ease;
}
.reveal.in { opacity: 1; transform: none; }
.reveal { will-change: transform, opacity; }

@keyframes ambientDrift {
  0% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(0, -10px, 0) scale(1.02); }
  100% { transform: translate3d(0, 0, 0) scale(1); }
}
@keyframes softGlow {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.52; }
}

/* NAV */
nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  padding: 1.5rem 0;
  transition: background 0.4s, padding 0.4s, border-color 0.4s;
  border-bottom: 1px solid transparent;
}
nav.scrolled {
  background: rgba(24,36,58,0.96);
  backdrop-filter: blur(18px);
  padding: 0.9rem 0;
  border-color: var(--border);
}
.nav-inner {
  display: flex; align-items: center;
  justify-content: space-between; gap: 1.5rem;
}
.nav-logo img { height: 90px; width: auto; display: block; }

.nav-links { display: flex; align-items: center; gap: 2.5rem; list-style: none; }
.nav-links a {
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 500; font-size: 0.82rem;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--cream-dim); text-decoration: none;
  transition: color 0.3s; position: relative;
}
.nav-links a::after {
  content: ''; position: absolute; bottom: -5px; left: 0;
  width: 0; height: 1px; background: var(--copper);
  transition: width 0.3s ease;
}
.nav-links a:hover { color: var(--copper); }
.nav-links a:hover::after { width: 100%; }

.nav-right { display: flex; align-items: center; gap: 1rem; }

.lang-toggle {
  display: flex; border: 1px solid var(--border);
  overflow: hidden; border-radius: 2px;
}
.lang-btn {
  padding: 0.38rem 0.75rem;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  cursor: pointer; border: none; background: transparent;
  color: var(--silver); transition: all 0.25s;
}
.lang-btn.active { background: var(--copper); color: var(--deep); }

.hamburger { display: none; flex-direction: column; gap: 5px; cursor: pointer; }
.hamburger span { display: block; width: 22px; height: 1.5px; background: var(--cream); transition: all 0.3s; }

.mobile-nav {
  display: none; position: fixed; inset: 0; z-index: 190;
  background: var(--deep); flex-direction: column;
  align-items: center; justify-content: center; gap: 2.5rem;
}
.mobile-nav.open { display: flex; }
.mobile-nav a {
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem; font-weight: 400;
  color: var(--cream); text-decoration: none;
  transition: color 0.3s;
}
.mobile-nav a:hover { color: var(--copper); }

/* HERO */
.hero {
  min-height: 100vh; display: flex; align-items: center;
  position: relative; overflow: hidden;
  padding: 9rem 0 5rem;
}
.hero-bg {
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 75% 55% at 68% 48%, rgba(61,80,112,0.45) 0%, transparent 65%),
    radial-gradient(ellipse 35% 35% at 15% 85%, rgba(232,145,90,0.07) 0%, transparent 55%);
}
.hero-panel {
  position: absolute; top: 0; right: 0;
  width: 42%; height: 100%;
  background: linear-gradient(155deg, rgba(44,63,88,0.18) 0%, rgba(44,63,88,0.05) 100%);
  clip-path: polygon(18% 0%, 100% 0%, 100% 100%, 0% 100%);
  border-left: 1px solid rgba(232,145,90,0.08);
}
.hero-ghost {
  position: absolute; right: -2%; top: 50%;
  transform: translateY(-50%);
  width: 52%; opacity: 0.055; pointer-events: none;
  color: var(--copper);
}

.hero-content { position: relative; z-index: 2; max-width: 680px; }

.hero-eyebrow {
  display: flex; align-items: center;
  gap: 1rem; margin-bottom: 2rem;
}
.hero-eyebrow::before {
  content: ''; width: 36px; height: 1px; background: var(--copper);
  flex-shrink: 0;
}

.hero h1 {
  font-size: clamp(3.2rem, 6.5vw, 6rem);
  font-weight: 300; line-height: 1.04;
  margin-bottom: 1.25rem;
}
.hero h1 em { font-style: italic; color: var(--copper); }

.hero-tagline {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(0.8rem, 1.6vw, 1rem);
  letter-spacing: 0.38em; text-transform: uppercase;
  color: var(--silver); margin-bottom: 3rem;
}

.hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

.btn-primary {
  display: inline-flex; align-items: center; gap: 0.7rem;
  padding: 0.95rem 1.9rem;
  background: var(--copper); color: var(--deep);
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 700; font-size: 0.82rem;
  letter-spacing: 0.14em; text-transform: uppercase;
  text-decoration: none; border: none; cursor: pointer;
  position: relative; overflow: hidden;
  transition: transform 0.2s;
}
.btn-primary::before {
  content: ''; position: absolute; inset: 0;
  background: var(--copper-light);
  transform: translateX(-101%);
  transition: transform 0.35s ease;
}
.btn-primary:hover::before { transform: translateX(0); }
.btn-primary:hover { transform: translateY(-1px); }
.btn-primary > * { position: relative; z-index: 1; }

.btn-ghost {
  display: inline-flex; align-items: center; gap: 0.7rem;
  padding: 0.95rem 1.9rem;
  background: transparent; color: var(--cream);
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 500; font-size: 0.82rem;
  letter-spacing: 0.14em; text-transform: uppercase;
  text-decoration: none;
  border: 1px solid rgba(240,235,226,0.25);
  transition: border-color 0.3s, color 0.3s;
}
.btn-ghost:hover { border-color: var(--copper); color: var(--copper); }

.scroll-cue {
  position: absolute; bottom: 2.5rem; left: 50%;
  transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center;
  gap: 0.6rem; z-index: 2; opacity: 0.7;
}
.scroll-cue span {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.62rem; letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--silver);
}
.scroll-line {
  width: 1px; height: 48px;
  background: linear-gradient(to bottom, var(--copper), transparent);
  animation: pulse 2.2s ease-in-out infinite;
}
@keyframes pulse { 0%,100%{opacity:.25} 50%{opacity:1} }

/* STATS STRIP */
.stats-strip {
  background: linear-gradient(180deg, rgba(52, 73, 103, 0.82), rgba(42, 60, 87, 0.82));
  border-top: 1px solid rgba(132,154,189,0.26);
  border-bottom: 1px solid rgba(132,154,189,0.26);
}
.stats-grid { display: grid; grid-template-columns: repeat(4,1fr); }
.stat {
  padding: 1.8rem 1.25rem; text-align: center;
  border-right: 1px solid rgba(132,154,189,0.2);
}
.stat:last-child { border-right: none; }
.stat-num {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.86rem; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--copper); line-height: 1.2;
  margin-bottom: 0.45rem;
}
.stat-lbl { font-size: 0.95rem; line-height: 1.45; color: #c9d6ea; }

/* SHARED SECTION */
section { padding: 7rem 0; }
.section-head { margin-bottom: 4rem; }
.section-head h2 {
  font-size: clamp(2.2rem, 4.5vw, 3.8rem);
  font-weight: 400; line-height: 1.1;
}
.section-head p {
  margin-top: 0.75rem;
  font-size: 0.95rem; line-height: 1.75;
  color: var(--silver); max-width: 480px;
}

/* SERVICES */
.services { background: var(--deep); }
.services-grid {
  display: grid; grid-template-columns: repeat(3,1fr);
  gap: 1px; background: var(--border);
  border: 1px solid var(--border);
}
.svc {
  background: var(--deep); padding: 2.5rem;
  position: relative; overflow: hidden;
  transition: background 0.35s;
}
.svc:hover { background: rgba(44,63,88,0.7); }
.svc::before {
  content: ''; position: absolute;
  top: 0; left: 0; width: 3px; height: 0;
  background: var(--copper);
  transition: height 0.4s ease;
}
.svc:hover::before { height: 100%; }
.svc-num {
  position: absolute; top: 1.25rem; right: 1.75rem;
  font-family: 'Cormorant Garamond', serif;
  font-size: 3.8rem; font-weight: 300;
  color: var(--navy-mid); line-height: 1;
  transition: color 0.35s;
}
.svc:hover .svc-num { color: rgba(232,145,90,0.12); }
.svc-icon { width: 44px; height: 44px; margin-bottom: 1.4rem; color: var(--copper); opacity: 0.85; }
.svc h3 { font-size: 1.45rem; font-weight: 500; margin-bottom: 0.6rem; }
.svc p { font-size: 0.88rem; line-height: 1.68; color: var(--silver); }

/* TRUST */
.trust { background: var(--navy); position: relative; overflow: hidden; }
.trust-ring {
  position: absolute; border: 1px solid var(--border-cu);
  border-radius: 50%; opacity: 0.25; pointer-events: none;
}
.trust-ring-1 { width:500px;height:500px; top:-150px; right:-150px; }
.trust-ring-2 { width:320px;height:320px; bottom:-120px; right:-60px; }

.trust-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 6rem; align-items: center; }
.trust-left h2 { font-size: clamp(2rem,4vw,3.4rem); font-weight: 400; line-height: 1.1; margin-bottom: 1.5rem; }
.trust-left p { font-size: 0.95rem; line-height: 1.75; color: var(--silver); }

.trust-feats { display: flex; flex-direction: column; gap: 2rem; }
.trust-feat { display: flex; gap: 1.25rem; align-items: flex-start; }
.feat-icon {
  flex-shrink: 0; width: 42px; height: 42px;
  border: 1px solid var(--border-cu);
  display: flex; align-items: center; justify-content: center;
  color: var(--copper);
}
.trust-feat h4 {
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 700; font-size: 0.88rem;
  letter-spacing: 0.08em; text-transform: uppercase;
  margin-bottom: 0.35rem;
}
.trust-feat p { font-size: 0.88rem; line-height: 1.65; color: var(--silver); }

/* REVIEWS */
.reviews { background: var(--deep); }
.reviews-nav { display: flex; justify-content: flex-end; gap: 0.6rem; margin: -1.5rem 0 1.25rem; }
.reviews-btn {
  width: 42px; height: 42px;
  border: 1px solid rgba(132,154,189,0.34);
  background: rgba(10,24,50,0.62);
  color: var(--cream);
  border-radius: 6px;
  cursor: pointer; font-size: 1.05rem;
  transition: border-color .24s ease, color .24s ease, transform .24s ease, background .24s ease;
}
.reviews-btn:hover { border-color: var(--copper); color: var(--copper); transform: translateY(-1px); }
.reviews-btn:disabled { opacity: .35; cursor: not-allowed; transform: none; }
.reviews-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 1.5rem; }
.review {
  background: linear-gradient(180deg, rgba(14,30,58,0.82), rgba(10,24,50,0.78));
  border: 1px solid rgba(132,154,189,0.28);
  border-radius: 10px;
  padding: 1.25rem;
  position: relative;
  transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s;
}
.review:hover {
  border-color: rgba(232,145,90,0.34);
  transform: translateY(-3px);
  box-shadow: 0 14px 30px rgba(4,10,22,0.34);
}
.stars { margin-bottom: 1.1rem; color: var(--copper); font-size: 0.85rem; letter-spacing: 2px; }
.review-body {
  font-family: 'Barlow', sans-serif;
  font-size: 0.95rem; line-height: 1.72;
  color: #d6e0ef;
  margin-bottom: 0.75rem;
  min-height: 8.8rem;
}
.review-foot {
  display: flex; align-items: center; gap: 1rem;
  border-top: 1px solid rgba(132,154,189,0.22);
  padding-top: 0.95rem;
}
.avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: rgba(61,80,112,0.5);
  border: 1px solid rgba(232,145,90,0.35);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.82rem; letter-spacing: 0.08em; color: var(--copper);
  flex-shrink: 0;
}
.reviewer strong {
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 600; font-size: 0.82rem; letter-spacing: 0.08em; text-transform: uppercase;
}
.reviewer span { font-size: 0.76rem; color: var(--silver); }
.review-src {
  margin-left: auto;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.68rem; letter-spacing: 0.1em;
  text-transform: uppercase; color: #9fb1cb;
  border: 1px solid rgba(132,154,189,0.24);
  border-radius: 4px;
  padding: 0.3rem 0.45rem;
  background: rgba(9,24,50,0.38);
}

/* LOCATION */
.location {
  background:
    radial-gradient(960px 420px at 15% -10%, rgba(232,145,90,0.08), transparent 62%),
    radial-gradient(1100px 460px at 100% 25%, rgba(44, 63, 88, 0.25), transparent 60%),
    linear-gradient(180deg, #081a35 0%, #07162f 100%);
}
.location-layout { display: grid; grid-template-columns: 1fr 1.55fr; gap: 1.4rem; align-items: stretch; }
.location .section-head { margin-bottom: 2.2rem; }
.location .section-head p { max-width: 700px; }
.location-card {
  border: 1px solid rgba(132, 154, 189, 0.28);
  background: rgba(10, 24, 50, 0.64);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  padding: 1.5rem 1.25rem;
  height: 100%;
}
.contact-row {
  display: flex; gap: 1rem; margin-bottom: 1rem;
  align-items: flex-start;
  padding: 0.9rem;
  border: 1px solid rgba(132, 154, 189, 0.2);
  border-radius: 8px;
  background: rgba(8, 22, 46, 0.44);
}
.contact-row:last-child { margin-bottom: 0; }
.contact-ico {
  flex-shrink: 0; width: 38px; height: 38px;
  border: 1px solid rgba(232,145,90,0.45);
  display: flex; align-items: center; justify-content: center;
  color: var(--copper);
  border-radius: 7px;
}
.contact-row strong {
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.72rem; letter-spacing: 0.15em;
  text-transform: uppercase; color: var(--silver);
  margin-bottom: 0.15rem;
}
.contact-row a { color: var(--cream); text-decoration: none; transition: color 0.3s; font-size: 0.95rem; }
.contact-row a:hover { color: var(--copper); }
.contact-row p { font-size: 0.95rem; }

.map-wrap {
  aspect-ratio: 4/3;
  border: 1px solid rgba(132, 154, 189, 0.3);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(10, 24, 50, 0.64);
  backdrop-filter: blur(8px);
  box-shadow: inset 0 0 0 1px rgba(132,154,189,0.14);
}
.map-wrap iframe { width:100%; height:100%; border:none; display:block; }

/* CONTACT FORM */
.contact-sec { background: var(--deep); }
.form-layout { display: grid; grid-template-columns: 1fr 1.2fr; gap: 6rem; align-items: start; }
.form-aside h2 { font-size: clamp(2rem,4vw,3.2rem); font-weight: 400; line-height: 1.1; margin-bottom: 1.5rem; }
.form-aside p { font-size: 0.95rem; line-height: 1.75; color: var(--silver); }
.call-box {
  margin-top: 2.5rem;
  padding: 1.75rem;
  border: 1px solid var(--border-cu);
  background: rgba(232,145,90,0.04);
}
.call-box .label { margin-bottom: 0.6rem; display: block; }
.call-box a {
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.1rem; font-weight: 400;
  color: var(--cream); text-decoration: none;
  display: block; transition: color 0.3s;
}
.call-box a:hover { color: var(--copper); }
.call-box small { font-size: 0.82rem; color: var(--silver); }

.form-group { margin-bottom: 1.25rem; }
.form-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

label {
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.7rem; font-weight: 600;
  letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--silver); margin-bottom: 0.45rem;
}
input, textarea, select {
  width: 100%;
  background: var(--navy);
  border: 1px solid var(--border);
  color: var(--cream);
  font-family: 'Barlow', sans-serif;
  font-size: 0.92rem; font-weight: 300;
  padding: 0.85rem 1rem;
  outline: none; appearance: none;
  transition: border-color 0.3s;
}
input::placeholder, textarea::placeholder { color: var(--muted); }
input:focus, textarea:focus, select:focus { border-color: var(--copper); }
textarea { resize: vertical; min-height: 110px; }
select option { background: var(--navy); }

.form-note { font-size: 0.78rem; color: var(--muted); margin-top: 0.85rem; }

/* FOOTER */
footer {
  background: linear-gradient(180deg, rgba(7, 18, 39, 0.9), rgba(7, 18, 39, 0.96));
  border-top: 1px solid rgba(132, 154, 189, 0.24);
  padding: 2.5rem 0;
  backdrop-filter: blur(10px);
}
.footer-inner {
  display: flex; align-items: center;
  justify-content: space-between; gap: 2rem; flex-wrap: wrap;
  padding-top: 0.4rem;
}
.footer-logo img { height: 60px; }
.footer-links { display: flex; gap: 2rem; list-style: none; flex-wrap: wrap; }
.footer-links a {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.8rem; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--cream-dim);
  text-decoration: none; transition: color 0.3s;
  position: relative;
}
.footer-links a::after {
  content: ''; position: absolute; bottom: -5px; left: 0;
  width: 0; height: 1px; background: var(--copper);
  transition: width 0.3s ease;
}
.footer-links a:hover::after { width: 100%; }
.footer-links a:hover { color: var(--copper); }
.footer-copy {
  font-size: 0.78rem;
  color: #9fb1cb;
  border: 1px solid rgba(132,154,189,0.24);
  padding: 0.55rem 0.75rem;
  border-radius: 4px;
  background: rgba(9, 24, 50, 0.35);
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .trust-layout { grid-template-columns: 1fr; gap: 3rem; }
  .location-layout { grid-template-columns: 1fr; }
  .form-layout { grid-template-columns: 1fr; gap: 3rem; }
  .stats-grid { grid-template-columns: repeat(2,1fr); }
  .stat:nth-child(2) { border-right: none; }
  .stat:nth-child(3) { border-top: 1px solid var(--border); border-right: 1px solid var(--border); }
  .stat:nth-child(4) { border-top: 1px solid var(--border); }
  .services-grid { grid-template-columns: repeat(2,1fr); }
}
@media (max-width: 768px) {
  .container { padding: 0 1.25rem; }
  nav { padding: 0.75rem 0; }
  .nav-logo img { height: 54px; }
  .nav-inner { gap: 0.85rem; }
  .nav-right { gap: 0.65rem; }
  .lang-btn { padding: 0.34rem 0.58rem; }
  .nav-links { display: none; }
  .hamburger { display: flex; }
  .mobile-nav {
    justify-content: flex-start;
    gap: 1.35rem;
    overflow-y: auto;
    padding: 6.5rem 1.25rem 2rem;
  }
  .mobile-nav a {
    font-size: clamp(1.85rem, 9vw, 2.45rem);
    line-height: 1;
  }
  section { padding: 4.5rem 0; }
  .section-head { margin-bottom: 2.5rem; }
  .hero {
    min-height: auto;
    padding: 7rem 0 3rem;
  }
  .hero h1 { font-size: 2.8rem; }
  .hero-tagline {
    font-size: 1.05rem;
    margin-bottom: 2rem;
  }
  .hero-ctas { flex-direction: column; }
  .hero-ctas .btn-primary,
  .hero-ctas .btn-ghost {
    width: 100%;
    justify-content: center;
    padding: 0.9rem 1.15rem;
  }
  .scroll-cue { display: none; }
  .services-grid { grid-template-columns: 1fr; }
  .reviews-grid { grid-template-columns: 1fr; }
  .reviews-nav { justify-content: center; margin-top: -0.8rem; }
  .form-row2 { grid-template-columns: 1fr; }
  .footer-inner { flex-direction: column; text-align: center; }
  .footer-links { justify-content: center; }
  .hero-ghost { display: none; }
}

/* Layered overrides */
nav {
  background: linear-gradient(180deg, rgba(7, 18, 39, 0.96), rgba(7, 18, 39, 0.84));
  border-bottom: 1px solid rgba(132, 154, 189, 0.24);
}
nav.scrolled { background: rgba(7, 18, 39, 0.96); }
.nav-logo img { height: 72px; }
.lang-toggle { border-color: rgba(132, 154, 189, 0.3); }

.hero {
  min-height: 100vh;
  isolation: isolate;
}
.hero::after {
  content: "";
  position: absolute;
  inset: -8%;
  background:
    radial-gradient(42% 35% at 72% 38%, rgba(232,145,90,0.13), transparent 72%),
    radial-gradient(30% 28% at 24% 62%, rgba(105,131,176,0.12), transparent 72%);
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 0;
  animation: ambientDrift 14s ease-in-out infinite;
}
.hero-bg, .hero-panel { display: none; }
.hero-tagline {
  font-family: 'Barlow', sans-serif;
  text-transform: none;
  letter-spacing: 0;
  font-size: clamp(1.1rem, 2vw, 2rem);
  line-height: 1.45;
  color: #d5dfef;
  max-width: 740px;
}
.stats-strip {
  background: rgba(52, 73, 103, 0.92);
  border-top: 1px solid rgba(132, 154, 189, 0.24);
  border-bottom: 1px solid rgba(132, 154, 189, 0.24);
}

.services {
  background:
    radial-gradient(1200px 520px at 80% -10%, rgba(44, 63, 88, 0.26), transparent 62%),
    linear-gradient(180deg, #081a35 0%, #07162f 100%);
}
.trust {
  background:
    radial-gradient(980px 460px at -10% 40%, rgba(232,145,90,0.08), transparent 65%),
    linear-gradient(180deg, #091c39 0%, #081a35 100%);
}
.reviews {
  background:
    radial-gradient(1050px 420px at 90% 15%, rgba(44, 63, 88, 0.24), transparent 60%),
    linear-gradient(180deg, #07162f 0%, #061329 100%);
}
.contact-sec {
  background:
    radial-gradient(800px 380px at 85% 0%, rgba(232,145,90,0.08), transparent 62%),
    linear-gradient(180deg, #081a35 0%, #061329 100%);
}
.services-grid { gap: 1rem; background: transparent; border: 0; }
.svc, .review, .trust-feat, .map-wrap, #bookForm {
  background: rgba(10, 24, 50, 0.64);
  border: 1px solid rgba(132, 154, 189, 0.3);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease, background .3s ease;
}
.svc:hover, .review:hover, .trust-feat:hover, .location-card:hover, .map-wrap:hover, #bookForm:hover {
  transform: translateY(-4px);
  border-color: rgba(232,145,90,0.34);
  box-shadow: 0 16px 36px rgba(4, 10, 22, 0.34);
}
.svc { padding: 2.25rem; }
.svc::before { width: 2px; }

.trust-feats { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.trust-feat { padding: 1.75rem 1.5rem; flex-direction: column; align-items: center; text-align: center; }
.feat-icon { border: 0; width: 62px; height: 62px; }

.call-box { border-radius: 10px; border-color: rgba(132, 154, 189, 0.3); background: rgba(10, 24, 50, 0.56); }
#bookForm { padding: 2rem; }
input, textarea, select {
  border-radius: 6px;
  background: rgba(12, 30, 62, 0.82);
  border-color: rgba(132, 154, 189, 0.32);
  color: #e8eef9;
}
input:focus, textarea:focus, select:focus {
  border-color: rgba(232, 145, 90, 0.84);
  box-shadow: 0 0 0 2px rgba(232, 145, 90, 0.16);
}
.form-note { color: #b8c8e0; }

.hero { padding: 9.5rem 0 4.5rem; }
.hero-wrap {
  position: relative;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 2rem;
  align-items: end;
}
.hero-card {
  justify-self: end;
  width: min(390px, 100%);
  margin-bottom: 2.5rem;
  border: 1px solid rgba(132, 154, 189, 0.35);
  border-radius: 20px;
  background: rgba(8, 22, 46, 0.64);
  backdrop-filter: blur(8px);
  padding: 1.5rem 1.75rem;
  box-shadow: 0 12px 34px rgba(4, 10, 22, 0.32);
  animation: softGlow 5.5s ease-in-out infinite;
}
.hero-card .stars { margin-bottom: .7rem; font-size: 2rem; letter-spacing: 6px; }
.hero-card p { font-size: 1.1rem; line-height: 1.45; color: #d2ddef; }
.hero-card strong { color: var(--copper); font-size: 1.9rem; font-family: 'Barlow Condensed', sans-serif; }
.hero-features {
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid rgba(132, 154, 189, 0.26);
  border-bottom: 1px solid rgba(132, 154, 189, 0.26);
}
.hero-feat { padding: 1.2rem 1rem; border-right: 1px solid rgba(132, 154, 189, 0.22); }
.hero-feat:last-child { border-right: 0; }
.hero-feat h4 {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: .92rem;
  letter-spacing: .14em;
  text-transform: uppercase;
  margin-bottom: .35rem;
}
.hero-feat p { font-size: .95rem; color: #aebed8; }

.contact-row { transition: transform .24s ease, border-color .24s ease, background .24s ease; }
.contact-row:hover {
  transform: translateX(3px);
  border-color: rgba(232,145,90,0.34);
  background: rgba(9, 24, 50, 0.62);
}
.btn-primary, .btn-ghost, .lang-btn { transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease, color .22s ease, background .22s ease; }
.btn-primary:hover, .btn-ghost:hover { box-shadow: 0 10px 24px rgba(4, 10, 22, 0.28); }
.lang-btn:hover { transform: translateY(-1px); }

@media (max-width: 768px) {
  .hero-wrap { grid-template-columns: 1fr; }
  .hero-card {
    justify-self: stretch;
    margin-bottom: 0;
    padding: 1.2rem;
    border-radius: 12px;
  }
  .hero-card .stars {
    font-size: 1.35rem;
    letter-spacing: 4px;
  }
  .hero-card p { font-size: 1rem; }
  .hero-card strong { font-size: 1.45rem; }
  .hero-features { grid-template-columns: 1fr; }
  .hero-feat { padding: 1rem 0.9rem; border-right: 0; border-bottom: 1px solid rgba(132, 154, 189, 0.2); }
  .hero-feat:last-child { border-bottom: 0; }
  .trust-feats { grid-template-columns: 1fr; }
  #bookForm { padding: 1.25rem; }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation: none !important;
    transition-duration: .01ms !important;
    scroll-behavior: auto !important;
  }
}

/* SERVICES FOOT */
.services-foot {
  margin-top: 1.8rem;
  border: 1px solid rgba(132, 154, 189, 0.26);
  border-radius: 8px;
  background: rgba(8, 22, 46, 0.56);
  display: grid;
  grid-template-columns: 1.3fr .9fr;
  align-items: center;
  overflow: hidden;
}
.services-foot-note {
  padding: 1.25rem 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
}
.services-foot-note strong {
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--copper);
  font-size: .92rem;
}
.services-foot-note span { color: #afbfd8; font-size: 1rem; }
.services-foot .btn-primary {
  margin: 0 1rem 0 0;
  justify-self: end;
  min-width: 270px;
  justify-content: center;
}
@media (max-width: 768px) {
  .services-foot { grid-template-columns: 1fr; }
  .services-foot .btn-primary { margin: 0 1rem 1rem; width: calc(100% - 2rem); }
}

/* SERVICE AREAS */
.areas {
  padding: 7rem 0 6rem;
  position: relative;
  background: linear-gradient(180deg, var(--deep) 0%, rgba(36, 50, 75, 0.35) 100%);
  border-top: 1px solid var(--border);
}
.areas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.85rem;
  margin-top: 2.75rem;
}
.area-card {
  border: 1px solid var(--border);
  background: rgba(9, 24, 50, 0.42);
  padding: 1.15rem 1.25rem;
  border-radius: 3px;
  transition: border-color 0.3s, transform 0.3s, background 0.3s;
}
.area-card:hover {
  border-color: rgba(232, 145, 90, 0.42);
  background: rgba(9, 24, 50, 0.62);
  transform: translateY(-2px);
}
.area-card strong {
  display: block;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.35rem;
  font-weight: 500;
  color: var(--cream);
  margin-bottom: 0.25rem;
}
.area-card span {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.74rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--copper);
  opacity: 0.86;
}
.areas-note {
  margin-top: 2rem;
  font-size: 0.95rem;
  color: var(--cream-dim);
  max-width: 760px;
  line-height: 1.7;
}

/* TOURIST / AIRPORT BAND */
.tourists { padding: 6rem 0; position: relative; overflow: hidden; }
.tourists::before {
  content: '';
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 80% 30%, rgba(232,145,90,0.10) 0%, transparent 60%),
    radial-gradient(ellipse 40% 60% at 10% 80%, rgba(61,80,112,0.30) 0%, transparent 60%);
  pointer-events: none;
}
.tourists-card {
  position: relative;
  border: 1px solid var(--border-cu);
  background: rgba(9, 24, 50, 0.55);
  padding: 3rem 3rem;
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 3rem;
  align-items: center;
}
.tourists-card h2 {
  font-size: clamp(1.85rem, 3.2vw, 2.65rem);
  font-weight: 300;
  line-height: 1.15;
  margin-top: 0.5rem;
}
.tourists-card h2 em { font-style: italic; color: var(--copper); }
.tourists-card p { color: var(--cream-dim); line-height: 1.75; margin-top: 1.25rem; }
.tourists-feats { display: flex; flex-direction: column; gap: 1.1rem; }
.tourists-feat { display: flex; gap: 1rem; align-items: flex-start; }
.tourists-feat .feat-icon {
  flex-shrink: 0;
  width: 38px; height: 38px;
  border: 1px solid var(--border-cu);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: var(--copper);
}
.tourists-feat strong {
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--cream);
  margin-bottom: 0.3rem;
}
.tourists-feat p { margin-top: 0; font-size: 0.93rem; color: var(--cream-dim); }

/* FAQ */
.faq {
  padding: 7rem 0;
  background: linear-gradient(180deg, rgba(36, 50, 75, 0.35) 0%, var(--deep) 100%);
  border-top: 1px solid var(--border);
}
.faq-list { margin-top: 2.75rem; max-width: 880px; margin-left: auto; margin-right: auto; }
.faq-item {
  border: 1px solid var(--border);
  background: rgba(9, 24, 50, 0.42);
  border-radius: 3px;
  margin-bottom: 0.75rem;
  transition: border-color 0.3s, background 0.3s;
}
.faq-item[open] { border-color: rgba(232, 145, 90, 0.32); background: rgba(9, 24, 50, 0.62); }
.faq-item summary {
  list-style: none;
  cursor: pointer;
  padding: 1.4rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--cream);
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after {
  content: '+';
  font-family: 'Barlow', sans-serif;
  font-size: 1.4rem;
  color: var(--copper);
  transition: transform 0.3s;
}
.faq-item[open] summary::after { content: '-'; }
.faq-answer { padding: 0 1.6rem 1.5rem; color: var(--cream-dim); line-height: 1.8; font-size: 0.96rem; }

@media (max-width: 768px) {
  .tourists-card { grid-template-columns: 1fr; padding: 2rem 1.5rem; gap: 2rem; }
  .areas-grid { grid-template-columns: repeat(2, 1fr); }
  .faq-item summary { font-size: 1.08rem; padding: 1.1rem 1.25rem; }
  .faq-answer { padding: 0 1.25rem 1.25rem; }
}

@media (max-width: 480px) {
  .container { padding: 0 1rem; }
  .hero h1 { font-size: 2.35rem; }
  .stats-grid { grid-template-columns: 1fr; }
  .stat {
    border-right: 0;
    border-top: 1px solid rgba(132,154,189,0.2);
  }
  .stat:first-child { border-top: 0; }
  .stat:nth-child(3) { border-right: 0; }
  .services-foot-note {
    align-items: flex-start;
    padding: 1rem;
  }
  .areas-grid { grid-template-columns: 1fr; }
  .tourists-card { padding: 1.5rem 1rem; }
  .tourists-feat { gap: 0.8rem; }
  .contact-row {
    gap: 0.8rem;
    padding: 0.8rem;
  }
  .call-box a { font-size: 1.55rem; }
  .footer-links {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Final mobile layout pass: keep this after layered overrides. */
@media (max-width: 768px) {
  .container {
    width: 100%;
    max-width: 100%;
    padding: 0 1rem;
  }

  nav {
    z-index: 240;
    padding: 0.65rem 0;
    background: rgba(7, 18, 39, 0.98);
    backdrop-filter: blur(14px);
  }

  .nav-inner {
    min-height: 58px;
    gap: 0.75rem;
  }

  .nav-logo img {
    height: 48px;
    max-width: 150px;
  }

  .nav-right {
    gap: 0.55rem;
    margin-left: auto;
  }

  .lang-toggle {
    flex-shrink: 0;
  }

  .hamburger {
    display: inline-flex;
    width: 42px;
    height: 42px;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border: 1px solid rgba(132, 154, 189, 0.34);
    border-radius: 8px;
    background: rgba(10, 24, 50, 0.72);
    position: relative;
    z-index: 230;
  }

  .hamburger span {
    width: 19px;
    height: 2px;
    border-radius: 999px;
  }

  .hamburger.active span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }

  .hamburger.active span:nth-child(2) {
    opacity: 0;
  }

  .hamburger.active span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  .mobile-nav {
    z-index: 210;
    justify-content: flex-start;
    align-items: stretch;
    gap: 0.35rem;
    overflow-y: auto;
    padding: 6rem 1rem 2rem;
    background:
      linear-gradient(180deg, rgba(7, 18, 39, 0.98), rgba(8, 22, 46, 0.98)),
      var(--deep);
  }

  .mobile-nav a {
    width: 100%;
    border-bottom: 1px solid rgba(132, 154, 189, 0.18);
    padding: 1rem 0.25rem;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    line-height: 1.1;
    text-transform: uppercase;
  }

  section {
    padding: 4rem 0;
  }

  .section-head {
    margin-bottom: 2rem;
  }

  .section-head h2,
  .trust-left h2,
  .form-aside h2 {
    font-size: 2.1rem;
    line-height: 1.08;
  }

  .hero {
    min-height: auto;
    padding: 6.25rem 0 2.5rem;
    align-items: flex-start;
    background-position: 64% center !important;
    background-size: cover !important;
  }

  .hero::after {
    inset: 0;
    background: linear-gradient(180deg, rgba(6, 18, 32, 0.16), rgba(6, 18, 32, 0.52));
  }

  .hero-wrap {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    align-items: start;
  }

  .hero-content {
    max-width: none;
  }

  .hero-eyebrow {
    margin-bottom: 1.25rem;
  }

  .hero h1 {
    max-width: 11ch;
    font-size: clamp(2.35rem, 13vw, 3.1rem);
    line-height: 0.98;
    margin-bottom: 1rem;
  }

  .hero-tagline {
    max-width: 22rem;
    font-size: 1rem;
    line-height: 1.45;
    margin-bottom: 1.45rem;
  }

  .hero-ctas {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .hero-ctas .btn-primary,
  .hero-ctas .btn-ghost {
    width: 100%;
    min-height: 48px;
    justify-content: center;
    padding: 0.85rem 1rem;
    text-align: center;
  }

  .hero-features {
    margin-top: 1.25rem;
  }

  .hero-feat {
    padding: 0.9rem 0.75rem;
  }

  .hero-card {
    width: 100%;
    margin: 0;
    padding: 1rem;
    border-radius: 10px;
  }

  .scroll-cue {
    display: none;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat,
  .stat:nth-child(2),
  .stat:nth-child(3),
  .stat:nth-child(4) {
    border-right: 0;
    border-top: 1px solid rgba(132,154,189,0.2);
    padding: 1.05rem 0.85rem;
  }

  .stat:first-child {
    border-top: 0;
  }

  .services-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.85rem;
  }

  .svc {
    min-height: 0;
    padding: 1.15rem;
    display: grid;
    grid-template-columns: 44px 1fr;
    column-gap: 0.9rem;
    align-items: start;
  }

  .svc-num {
    top: 0.7rem;
    right: 0.8rem;
    font-size: 2rem;
    opacity: 0.42;
  }

  .svc-icon {
    grid-row: span 2;
    width: 34px;
    height: 34px;
    margin: 0.1rem 0 0;
  }

  .svc h3 {
    padding-right: 2.4rem;
    font-size: 1.18rem;
    line-height: 1.15;
    margin: 0 0 0.4rem;
  }

  .svc p {
    font-size: 0.9rem;
    line-height: 1.55;
  }

  .services-foot {
    margin-top: 1rem;
  }

  .services-foot-note {
    padding: 1rem;
    align-items: flex-start;
  }

  .services-foot .btn-primary {
    width: calc(100% - 2rem);
    min-width: 0;
    margin: 0 1rem 1rem;
  }

  .trust {
    overflow: visible;
  }

  .trust-layout {
    grid-template-columns: 1fr;
    gap: 1.6rem;
  }

  .trust-left p {
    font-size: 0.95rem;
    line-height: 1.65;
  }

  .mmm-owner-card {
    align-items: flex-start;
    padding: 0.85rem;
  }

  .trust-feats {
    grid-template-columns: 1fr;
    gap: 0.85rem;
  }

  .trust-feat {
    min-height: 0;
    padding: 1rem;
    display: grid;
    grid-template-columns: 42px 1fr;
    gap: 0.85rem;
    align-items: start;
    text-align: left;
  }

  .trust-feat .feat-icon {
    width: 42px;
    height: 42px;
  }

  .trust-feat h4 {
    margin-bottom: 0.25rem;
  }

  .trust-feat p {
    font-size: 0.9rem;
    line-height: 1.55;
  }

  .reviews-grid,
  .location-layout,
  .form-layout,
  .tourists-card {
    grid-template-columns: 1fr;
  }

  .review-body {
    min-height: 0;
  }
}

@media (max-width: 420px) {
  .nav-logo img {
    height: 42px;
    max-width: 125px;
  }

  .lang-btn {
    padding: 0.3rem 0.48rem;
    font-size: 0.64rem;
  }

  .hamburger {
    width: 38px;
    height: 38px;
  }

  .hero {
    background-position: 68% center !important;
  }

  .hero h1 {
    font-size: 2.28rem;
  }

  .areas-grid {
    grid-template-columns: 1fr;
  }
}
`;

export default sourceStyles;
